import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Trash2, ArrowLeft, Lock } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import FunnelLayout from "@/components/app/FunnelLayout";
import ExitIntentModal from "@/components/app/ExitIntentModal";
import QuizLink from "@/components/app/QuizLink";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const KINDS = [
  { id: "bank_loan", label: "הלוואה בנקאית", color: "#d97706" },
  { id: "credit", label: "פריסת אשראי / הלוואת אשראי", color: "#ef4444" },
  { id: "overdraft", label: "מינוס קבוע / אחר", color: "#44403c" },
];

let uid = 0;
const nextId = () => `row_${++uid}`;

export default function FunnelGuide2() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([
    { id: nextId(), kind: "bank_loan", amount: 1500 },
    { id: nextId(), kind: "credit", amount: 2000 },
    { id: nextId(), kind: "overdraft", amount: 1000 },
  ]);

  const total = useMemo(
    () => rows.reduce((s, r) => s + (Number(r.amount) || 0), 0),
    [rows]
  );

  const pieData = useMemo(() => {
    const groups = {};
    rows.forEach((r) => {
      const k = KINDS.find((x) => x.id === r.kind);
      groups[r.kind] = groups[r.kind] || { name: k.label, value: 0, color: k.color };
      groups[r.kind].value += Number(r.amount) || 0;
    });
    return Object.values(groups).filter((g) => g.value > 0);
  }, [rows]);

  const updateRow = (id, field, value) =>
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, [field]: value } : r)));

  const removeRow = (id) => setRows((rs) => rs.filter((r) => r.id !== id));

  const addRow = () =>
    setRows((rs) => [...rs, { id: nextId(), kind: "bank_loan", amount: 0 }]);

  return (
    <FunnelLayout
      step={2}
      title="מיפוי החוסמים"
      loadingMessages={["טוען את מחשבון המיפוי...", "מכין את הנוסחה..."]}
    >
      {(lead) => (
        <>
          {/* Section 1: Why scary to check the balance */}
          <div className="bg-white rounded-3xl border border-black/5 p-6 md:p-8 mb-10 shadow-[0_8px_30px_rgba(0,0,0,0.04)]">
            <h2 className="text-2xl md:text-3xl font-[800] mb-4">למה כל כך מפחיד לבדוק את היתרה?</h2>
            <div className="space-y-4 text-stone-700 leading-[1.75]">
              <p>
                זה קורה לכולם. אתם יודעים שאתם צריכים לבדוק מה מצב החשבון, אבל פשוט דוחים את זה. הראש ממציא תירוצים: "אין לי זמן עכשיו", "אני יודע פחות או יותר מה המצב", או הקלאסי – "זה רק יעשה לי מצב רוח רע, נבדוק אחרי שתיכנס המשכורת".
              </p>
              <p>
                ההימנעות הזו היא לא עצלנות. היא <strong className="text-brand-amber">מנגנון הגנה פסיכולוגי</strong>. אנחנו מפחדים מה"אמת" כי אנחנו חושבים שהיא מעידה עלינו שאנחנו כישלון.
              </p>
              <div className="bg-amber-50 rounded-2xl p-5 border border-amber-200">
                <h3 className="font-[800] mb-2">אבל הנה הסוד:</h3>
                <p className="leading-[1.7]">
                  הפחד מהמספרים <em>תמיד</em>, אבל תמיד, גדול יותר מהמספרים עצמם. ברגע שמניחים את הכל על השולחן, החרדה העמומה מתחלפת בבעיה קונקרטית. ובעיה קונקרטית – אפשר לפתור.
                </p>
              </div>
            </div>
          </div>

          {/* Section 2: Interactive mapping tool */}
          <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgba(0,0,0,0.04)] border border-black/5 p-5 md:p-8 mb-8">
            <h2 className="text-2xl md:text-3xl font-[800] mb-2">כלי המיפוי האינטראקטיבי</h2>
            <p className="text-stone-600 mb-5 leading-[1.6]">
              הוציאו את הדפים, פתחו את האפליקציה של הבנק והאשראי. בואו נרכיב את הפאזל של "המשכנתה השנייה" שלכם.
            </p>
            <div className="bg-stone-50 rounded-xl p-3 mb-6 text-sm text-stone-600 flex items-center gap-2" data-testid="privacy-note">
              <Lock className="w-4 h-4 text-stone-500 flex-shrink-0" />
              <span>הנתונים שאתם מזינים כאן נשארים על המכשיר שלכם בלבד ואינם נשמרים בשום מקום.</span>
            </div>

            <h3 className="text-lg font-[800] mb-4">הזינו את ההתחייבויות שלכם:</h3>
            <div className="space-y-3" data-testid="commitments-list">
              <AnimatePresence>
                {rows.map((row) => (
                  <motion.div
                    key={row.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ duration: 0.25 }}
                    className="grid grid-cols-12 gap-2 items-center"
                    data-testid={`commitment-row-${row.id}`}
                  >
                    <div className="col-span-7 sm:col-span-6">
                      <Select
                        value={row.kind}
                        onValueChange={(v) => updateRow(row.id, "kind", v)}
                      >
                        <SelectTrigger className="h-12 rounded-xl">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {KINDS.map((k) => (
                            <SelectItem key={k.id} value={k.id}>
                              {k.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-4 sm:col-span-5 relative">
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 font-bold">₪</span>
                      <Input
                        type="number"
                        value={row.amount}
                        onChange={(e) =>
                          updateRow(row.id, "amount", Number(e.target.value) || 0)
                        }
                        className="h-12 rounded-xl pr-8 font-bold"
                        placeholder="0"
                      />
                    </div>
                    <button
                      onClick={() => removeRow(row.id)}
                      data-testid={`remove-row-${row.id}`}
                      className="col-span-1 h-10 w-10 rounded-full hover:bg-red-50 text-red-500 flex items-center justify-center transition"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
            <Button
              onClick={addRow}
              variant="outline"
              data-testid="add-row-btn"
              className="mt-5 w-full h-12 rounded-xl border-dashed font-bold"
            >
              <Plus className="w-4 h-4 me-2" />
              הוסף התחייבות נוספת
            </Button>
          </div>

          {/* Section 3: Total counter */}
          <div className="bg-gradient-to-tr from-brand-stone to-brand-charcoal text-white rounded-3xl p-6 md:p-10 mb-10 relative overflow-hidden">
            <div className="absolute -top-20 -left-20 w-64 h-64 rounded-full bg-brand-amber/20 blur-3xl" />
            <div className="relative">
              <h3 className="text-2xl font-[800] mb-2">תמונת המצב של "המשכנתה השנייה"</h3>
              <p className="text-stone-300 mb-6 text-sm">
                זהו סך הכסף שיוצא מהתזרים שלכם כל חודש (ללא המשכנתה על הבית/שכירות)
              </p>

              <div className="grid md:grid-cols-2 items-center gap-6">
                <div>
                  <div className="text-sm text-stone-400 mb-1">סה"כ החזר חודשי נסתר:</div>
                  <motion.div
                    key={total}
                    initial={{ scale: 0.9, opacity: 0.5 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.4 }}
                    className="text-5xl md:text-6xl font-[800] text-brand-amber"
                    data-testid="total-counter"
                  >
                    ₪{total.toLocaleString()}
                  </motion.div>
                  {total > 0 && (
                    <p className="mt-4 text-stone-300 text-sm leading-relaxed">
                      <strong className="text-emerald-400">סכום משמעותי!</strong> איחוד חובות וניהול נכון יכולים להחזיר לכם
                      בין <strong>1,000 ל-2,500 ₪</strong> בכל חודש לתזרים.
                    </p>
                  )}
                </div>
                <div className="h-48 min-h-[160px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieData.length > 0 ? pieData : [{ name: "אין נתונים", value: 1, color: "#44403c" }]}
                        cx="50%"
                        cy="50%"
                        innerRadius={45}
                        outerRadius={75}
                        paddingAngle={3}
                        dataKey="value"
                      >
                        {(pieData.length > 0 ? pieData : [{ color: "#44403c" }]).map((d, i) => (
                          <Cell key={d.name || `slice-${i}`} fill={d.color} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "#292524",
                          border: "1px solid rgba(255,255,255,0.2)",
                          borderRadius: 12,
                          color: "#fff",
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </div>

          {/* Section 4: Property as solution */}
          <div className="bg-white rounded-3xl p-6 md:p-8 border border-black/5 mb-8">
            <h2 className="text-2xl md:text-3xl font-[800] mb-4">יש לכם נכס? כך נפרק את המספר שראיתם למעלה</h2>
            <div className="space-y-3 text-stone-700 leading-[1.75]">
              <p>
                עכשיו כשאתם רואים מול העיניים את סך "המשכנתה השנייה" שלכם, הגיע הזמן לחתוך אותה. אם יש בבעלותכם דירה, אתם יכולים להשתמש בתהליך של <strong className="text-brand-amber">איחוד הלוואות</strong> ולהעביר את כל החובות היקרים האלו תחת המטרייה של המשכנתה.
              </p>
              <p>
                התוצאה? הפער בריביות יגרום להחזר החודשי שלכם לצנוח באופן דרמטי, והכסף יחזור ישירות לתזרים שלכם.
              </p>
            </div>
          </div>

          {/* Section 5: What to do now */}
          <div className="bg-amber-50 rounded-3xl border border-amber-200 p-6 md:p-8 mb-10">
            <h3 className="text-xl md:text-2xl font-[800] mb-5">מה עושים עכשיו?</h3>
            <div className="space-y-4">
              {[
                { id: "map", content: <>קחו את <strong>הסכום הכולל</strong> שיצא לכם במחשבון המיפוי למעלה.</> },
                { id: "quiz", content: <>כנסו <QuizLink lead={lead} source="guide2_what_now">לשאלון האבחון המהיר שלנו</QuizLink> (לוקח בדיוק 2 דקות).</> },
                { id: "discover", content: <>גלו מיד האם הנכס שלכם מאפשר לאחד את ההלוואות, וכמה כסף פנוי זה ייצר לכם בכל חודש.</> },
              ].map((step, i) => (
                <div key={step.id} className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-brand-amber text-white font-[800] flex items-center justify-center flex-shrink-0">
                    {i + 1}
                  </div>
                  <p className="text-stone-700 leading-[1.7] pt-1">{step.content}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Closing */}
          <div className="bg-white rounded-3xl p-6 md:p-8 border border-black/5 mb-10 text-center">
            <h2 className="text-2xl md:text-3xl font-[800] mb-3">עשיתם את הצעד הקשה ביותר.</h2>
            <p className="text-stone-700 leading-[1.7] max-w-2xl mx-auto">
              אם מיפיתם הכל והגעתם לסכום שמבהיל אתכם – <strong className="text-brand-amber">מעולה</strong>. זה אומר שיש לנו עם מה לעבוד. הסכום הזה הוא בדיוק החמצן שאנחנו הולכים להחזיר אליכם.
            </p>
          </div>

          {/* Next */}
          <div className="flex flex-col items-center gap-3 py-6">
            <Button
              data-testid="funnel-next-btn"
              onClick={() => navigate(`/funnel/${lead.lead_id}/3`)}
              className="h-14 px-10 rounded-full bg-brand-amber hover:bg-brand-amberHover text-white font-bold pulse-cta"
            >
              למדריך מס' 3: הפתרון
              <ArrowLeft className="w-5 h-5 me-2" />
            </Button>
          </div>
          <ExitIntentModal lead={lead} source="guide2" />
        </>
      )}
    </FunnelLayout>
  );
}
